import { Space, Typography, Button, Upload, type UploadProps, message } from 'antd';
import { saveAs } from 'file-saver';
import { isEmpty } from 'lodash/fp';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CompanyTheme } from '../../../../api/types';
import CancelButton from '../../../../components/modal/CancelButton';
import OkButton from '../../../../components/modal/OkButton';
import { useThemeComponents } from '../../../../themes';
import { applyHeaderStyle, getDataFromExcel, getWorkbookToExport } from '../../../../utilities/excel';
import { allowXlsxOnly, validateExtensions } from '../../../../utilities/extensions';
import { type WebsiteModuleFormValues } from './WebsiteModuleForm';

const EXCEL_HEADERS = {
    'Region *': 'region',
    'Country *': 'country',
    'Page URL *': 'url',
};

export type OnImportHandler = (values: Omit<WebsiteModuleFormValues['options'], '__typename'>) => unknown;
type ImportModalProps = {
    open: boolean;
    onCancel: () => unknown;
    onImport: OnImportHandler;
};
const Container = styled(Space)`
    width: 100%;
    justify-content: center;
`;
const Text = styled(Typography)`
    text-align: center;
`;
const StyledUpload = styled(Upload)`
    margin-left: 10px;
`;
export const StyledList = styled.ul`
    max-height: 100px;
    overflow: auto;
`;

const ImportModal = ({ open, onCancel, onImport }: ImportModalProps) => {
    const { t } = useTranslation('websiteModuleDetails');
    const { theme, Modal } = useThemeComponents();

    const downloadTemplate = useCallback(async () => {
        const workbook = await getWorkbookToExport(EXCEL_HEADERS);
        const worksheet = workbook.getWorksheet(1);

        applyHeaderStyle(worksheet);

        await workbook.xlsx.writeBuffer().then(data => {
            const blob = new Blob([data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            saveAs(blob, `Country Region Template.xlsx`);
        });
    }, []);
    const [errors, setErrors] = useState<string[] | null>(null);
    const hasError = !isEmpty(errors);
    const importExcel = useCallback<UploadProps['onChange']>(
        async ({ file }) => {
            if (file instanceof File) {
                const blob = new Blob([file], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
                });
                const buffer = await blob.arrayBuffer();

                try {
                    const { errors, data } = await getDataFromExcel({ excel: buffer, mappedHeaders: EXCEL_HEADERS });

                    if (errors.length === 0) {
                        if (data) {
                            onImport({ locations: data });
                            onCancel();
                        }
                    } else {
                        setErrors(errors);
                    }
                } catch (error) {
                    console.error(error);
                }
            }
        },
        [onCancel, onImport, setErrors]
    );
    const onCancelAndResetErrors = useCallback(() => {
        onCancel();
        setErrors(null);
    }, [onCancel]);

    const beforeUpload = useCallback<UploadProps['beforeUpload']>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );

    const content = !hasError ? (
        <Container direction="vertical">
            <Text>{t('websiteModuleDetails:modals.import.message')}</Text>
            <Text>{t('websiteModuleDetails:modals.import.note')}</Text>
            <Container direction="horizontal">
                <Button onClick={downloadTemplate} type="link">
                    {t('websiteModuleDetails:modals.import.buttons.downloadTemplate')}
                </Button>
            </Container>
        </Container>
    ) : (
        <Container direction="vertical">
            <Text>{t('websiteModuleDetails:modals.import.errorMessage')}</Text>
            <StyledList>
                {errors?.map((error, index) => (
                    <li key={index.toString()}>
                        <Typography>{error}</Typography>
                    </li>
                ))}
            </StyledList>
        </Container>
    );

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="cancel" onClick={onCancelAndResetErrors}>
                {t('websiteModuleDetails:modals.import.buttons.cancel')}
            </CancelButton>
        );

        if (hasError) {
            return [cancelButton];
        }

        const okButton = (
            <StyledUpload
                accept={allowXlsxOnly}
                beforeUpload={beforeUpload}
                onChange={importExcel}
                showUploadList={false}
                style={{ marginLeft: '1rem' }}
            >
                <OkButton key="upload">{t('websiteModuleDetails:modals.import.buttons.upload')}</OkButton>
            </StyledUpload>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [hasError, onCancelAndResetErrors, beforeUpload, importExcel, theme, t]);

    return (
        <Modal
            footer={footer}
            onCancel={onCancelAndResetErrors}
            open={open}
            title={t('websiteModuleDetails:modals.import.title')}
        >
            {content}
        </Modal>
    );
};

const useImportLocationsModal = (onImport: OnImportHandler) => {
    const [open, setOpen] = useState(false);

    const handlers = useMemo(() => ({ onCancel: () => setOpen(false), openModal: () => setOpen(true) }), [setOpen]);

    return {
        openModal: handlers.openModal,
        render: () => <ImportModal onCancel={handlers.onCancel} onImport={onImport} open={open} />,
    };
};

export default useImportLocationsModal;
